import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";

import Loading from "../../../Loading";
import { usePatientInsuranceVerifications } from "../../hooks";

interface InsurancePolicyTabProps {
  isLoading: boolean;
  insuranceData: PatientInsuranceMessage[];
  patientId: string;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <Box sx={{ marginBottom: 0.5 }}>
      <Typography variant="body2" color="text.secondary" component="span">
        {label}:{" "}
      </Typography>
      <Typography variant="body2" component="span">
        {value || "-"}
      </Typography>
    </Box>
  );
}

function PolicyListItem({
  insurance,
  isSelected,
  onClick,
}: {
  insurance: PatientInsuranceMessage;
  isSelected: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();
  const detail = insurance.insurance;

  return (
    <ListItemButton
      selected={isSelected}
      onClick={onClick}
      sx={{
        borderRadius: 1,
        mb: 0.5,
        flexDirection: "column",
        alignItems: "flex-start",
        py: 1.5,
        "&.Mui-selected": {
          backgroundColor: `${theme.palette.primary.light}20`,
          borderLeft: `3px solid ${theme.palette.primary.main}`,
        },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {detail?.payerName || "Unknown Payer"}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {detail?.policyNumber
          ? `Policy #${detail?.policyNumber}`
          : "No policy number"}
      </Typography>
    </ListItemButton>
  );
}

function PolicyDetails({ insurance }: { insurance: PatientInsuranceMessage }) {
  const theme = useTheme();
  const detail = insurance.insurance;

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, mb: 0.5, color: "primary.main" }}
      >
        {detail?.payerName || "Unknown Payer"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Policy Number: {detail?.policyNumber || "-"}
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Policy Information
      </Typography>
      <InfoRow label="Group Name" value={detail?.groupName ?? null} />
      <InfoRow label="Group Number" value={detail?.groupNumber ?? null} />

      <Divider sx={{ margin: theme.spacing(2, 0) }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Subscriber Information
      </Typography>
      <InfoRow
        label="Name"
        value={
          detail?.subscriberFirstName || detail?.subscriberLastName
            ? `${detail?.subscriberFirstName || ""} ${detail?.subscriberMiddleName || ""} ${detail?.subscriberLastName || ""}`.trim()
            : (detail?.subscriberName ?? null)
        }
      />
      <InfoRow label="DOB" value={formatDate(detail?.subscriberDob ?? null)} />
      <InfoRow
        label="Relationship"
        value={
          insurance.wieldyRelationshipToSubscriber ||
          insurance.relationshipToSubscriber
        }
      />

      <Divider sx={{ margin: theme.spacing(2, 0) }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Payer Contact
      </Typography>
      <InfoRow label="Address" value={detail?.payerAddress ?? null} />
      <InfoRow label="State" value={detail?.payerState ?? null} />
      <InfoRow label="Zipcode" value={detail?.payerZipcode ?? null} />
      <InfoRow label="Phone" value={detail?.payerPhoneNumber ?? null} />

      {insurance.sourceName && (
        <>
          <Divider sx={{ margin: theme.spacing(2, 0) }} />
          <InfoRow label="Source" value={insurance.sourceName} />
        </>
      )}
    </Box>
  );
}

function VerificationListItem({
  verification,
}: {
  verification: PatientInsuranceVerificationSummary;
}) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {verification.verificationStatus}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {verification.verificationOutcome || "No outcome"}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {verification.verifiedAt
          ? `Verified: ${formatDate(verification.verifiedAt)}`
          : "Not verified yet"}
      </Typography>
      {verification.networkStatus && (
        <Typography variant="caption" color="text.secondary" display="block">
          {verification.networkStatus.replace(/_/g, " ")}
        </Typography>
      )}
    </Box>
  );
}

const NO_VERIFICATIONS: PatientInsuranceVerificationSummary[] = [];

export function InsurancePolicyTab({
  isLoading,
  insuranceData,
  patientId,
}: InsurancePolicyTabProps) {
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Sort by updatedAt descending (most recent first)
  const sortedInsuranceData = useMemo(
    () =>
      [...insuranceData].sort((a, b) => {
        if (!a.updatedAt && !b.updatedAt) return 0;
        if (!a.updatedAt) return 1;
        if (!b.updatedAt) return -1;
        return (
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      }),
    [insuranceData],
  );

  const selectedInsurance = sortedInsuranceData[selectedIndex];

  // Reset selection when data changes
  useEffect(() => {
    if (sortedInsuranceData.length > 0) {
      setSelectedIndex(0);
    }
  }, [sortedInsuranceData]);

  const verificationsQuery = usePatientInsuranceVerifications(
    patientId,
    selectedInsurance?.insuranceKey,
  );
  const verifications = verificationsQuery.data ?? NO_VERIFICATIONS;
  const verificationsLoading = verificationsQuery.isLoading;

  if (isLoading) {
    return (
      <Box sx={{ height: "12rem" }}>
        <Loading />
      </Box>
    );
  }

  if (sortedInsuranceData.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "12rem",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No insurance records found for this patient.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 2, minHeight: "400px" }}>
      {/* Left Panel - Policy List */}
      <Paper
        variant="outlined"
        sx={{
          width: "240px",
          flexShrink: 0,
          p: 1,
          overflow: "auto",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, px: 1, py: 0.5, color: "text.secondary" }}
        >
          Policies ({sortedInsuranceData.length})
        </Typography>
        <List disablePadding>
          {sortedInsuranceData.map((insurance, index) => (
            <PolicyListItem
              key={insurance.wieldyId}
              insurance={insurance}
              isSelected={index === selectedIndex}
              onClick={() => setSelectedIndex(index)}
            />
          ))}
        </List>
      </Paper>

      {/* Middle Panel - Policy Details */}
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          p: theme.spacing(3),
          overflow: "auto",
        }}
      >
        {selectedInsurance && <PolicyDetails insurance={selectedInsurance} />}
      </Paper>

      {/* Right Panel - Verifications */}
      <Paper
        variant="outlined"
        sx={{
          width: "280px",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            px: 2,
            py: 1.5,
            color: "text.secondary",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          Verifications ({verifications.length})
        </Typography>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {verificationsLoading && (
            <Box sx={{ p: 2 }}>
              <Loading />
            </Box>
          )}
          {!verificationsLoading && verifications.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No verifications found
              </Typography>
            </Box>
          )}
          {!verificationsLoading &&
            verifications.length > 0 &&
            verifications.map((verification) => (
              <VerificationListItem
                key={verification.wieldyId}
                verification={verification}
              />
            ))}
        </Box>
      </Paper>
    </Box>
  );
}
